import React, { useEffect, useMemo, useRef, useState } from "react";
import { AlertCircle, ArrowUpRight, CalendarCheck, CalendarPlus, ChevronDown, Euro, FileSignature, FolderPlus, SlidersHorizontal, UserPlus } from "lucide-react";
import { euro } from "../components/practices/AdvancePanel";
import { AiPrioritiesButton, AiPrioritiesModal } from "../components/dashboard/AiPriorities";
import { SisterChecks, collectSisterChecks } from "../components/catasto/SisterChecks";
import { AnomaliesCard, ListView, NextActionsCard, WaitingCard, WorkloadCard } from "../components/dashboard/ControlRoomCards";
import { DEFAULT_ECONOMICS_METRICS, EconomicsMetricsPicker, EconomicsSummary } from "../components/dashboard/EconomicsSummary";
import { GanttChart } from "../components/dashboard/GanttChart";
import { GanttGuide } from "../components/dashboard/GanttGuide";
import { OtherModules } from "../components/dashboard/OtherModules";
import { PracticePreview } from "../components/dashboard/PracticePreview";
import { formatDate, practiceApi } from "../lib/practiceApi";
import { buildGreeting } from "../lib/greetings";
import { CAT_STATES } from "../components/catasto/constants";
import "./overview.css";
import "../components/dashboard/controlroom.css";

const RANGES = [["30", "30 giorni"], ["90", "3 mesi"], ["180", "6 mesi"], ["365", "1 anno"]];
const QUICK = [["all", "Tutte"], ["studio", "Solo studio"], ["esterne", "Solo attese esterne"], ["late", "In ritardo"], ["catasto", "Catasto"]];
const DEFAULT_LAYOUT = { show_gantt: true, show_economics: true, show_waiting: true, show_anomalies: true, show_modules: false, gantt_range: "90", gantt_view: "gantt", economics_metrics: DEFAULT_ECONOMICS_METRICS };

export default function OverviewPage({ data, team, user, go, openRecord, onCreate }) {
  const [layout, setLayout] = useState(DEFAULT_LAYOUT);
  const [tuning, setTuning] = useState(false);
  const [preferenceError, setPreferenceError] = useState("");
  const [preferencesSaving, setPreferencesSaving] = useState(0);
  const preferenceQueue = useRef(Promise.resolve());
  const localChanges = useRef({});
  const [control, setControl] = useState(null);
  const canViewEconomics = control?.can_view_economics === true;
  const [gantt, setGantt] = useState(null);
  const [ganttError, setGanttError] = useState("");
  const [startDate, setStartDate] = useState("");
  const [scopeAll, setScopeAll] = useState(false);
  const [quick, setQuick] = useState("all");
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [advanced, setAdvanced] = useState({ responsible: "", type: "", client: "", year: "", invoice: false, catasto: "" });
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [signed, setSigned] = useState([]);
  const [ai, setAi] = useState({ open: false, loading: false, rows: [], error: "" });
  const [modules, setModules] = useState(false);
  const [studioEvents, setStudioEvents] = useState([]);

  useEffect(() => {
    practiceApi("/preferences").then(prefs => {
      const saved = prefs.dashboard_layout || {};
      setLayout({ ...DEFAULT_LAYOUT, ...saved, ...localChanges.current });
      setModules(!!saved.show_modules);
    }).catch(() => {});
    practiceApi("/greetings").then(res => setStudioEvents([...res.custom, ...res.automatic])).catch(() => {});
    practiceApi("/estimates").then(rows => setSigned(rows.filter(r => r.signature && !r.practice_id))).catch(() => {});
  }, []);

  const cadastralFilter = quick === "catasto" || !!advanced.catasto;
  const filteredView = cadastralFilter || quick !== "all" || Object.values(advanced).some(Boolean);
  useEffect(() => {
    let cancelled = false;
    setGanttError("");
    const params = new URLSearchParams({ range: layout.gantt_range, scope_mode: scopeAll || filteredView ? "all" : "relevant" });
    if (cadastralFilter) params.set("catasto", "true");
    if (advanced.catasto) params.set("stato_catasto", advanced.catasto);
    if (startDate) params.set("start_date", startDate);
    practiceApi(`/dashboard/gantt?${params}`).then(result => { if (!cancelled) setGantt(result); })
      .catch(err => { if (!cancelled) { setGantt(null); setGanttError(err.message); } });
    return () => { cancelled = true; };
  }, [layout.gantt_range, scopeAll, cadastralFilter, filteredView, advanced.catasto, startDate]);

  useEffect(() => {
    const refresh = () => practiceApi("/dashboard/control-room").then(setControl).catch(() => setControl(null));
    refresh(); window.addEventListener("focus", refresh);
    return () => window.removeEventListener("focus", refresh);
  }, []);

  function persist(changes) {
    localChanges.current = { ...localChanges.current, ...changes };
    setLayout(previous => ({ ...previous, ...changes }));
    setPreferenceError(""); setPreferencesSaving(count => count + 1);
    preferenceQueue.current = preferenceQueue.current.then(() => practiceApi("/preferences", { method: "PUT", body: JSON.stringify({ data: { dashboard_layout: changes } }) }))
      .catch(err => setPreferenceError(`Preferenze non salvate: ${err.message}`))
      .finally(() => setPreferencesSaving(count => count - 1));
  }

  async function runAi() {
    setAi({ open: true, loading: true, rows: [], error: "" });
    try {
      const res = await practiceApi("/dashboard/ai-priorities", { method: "POST", body: JSON.stringify({ note: "" }) });
      setAi({ open: true, loading: false, rows: res.priorities || [], error: "" });
    } catch (err) { setAi({ open: true, loading: false, rows: [], error: err.message }); }
  }

  const rows = useMemo(() => {
    const all = gantt?.rows || [];
    const filtered = all.filter(row => {
      if (quick === "studio" && !row.has_studio_actions) return false;
      if (quick === "esterne" && !row.has_external_waits) return false;
      if (quick === "late" && !row.has_overdue_studio) return false;
      if (cadastralFilter && !row.catasto_attivo) return false;
      if (advanced.catasto && row.stato_catasto !== advanced.catasto) return false;
      if (advanced.invoice && !row.economics.to_invoice) return false;
      if (advanced.responsible && row.responsible_id !== advanced.responsible) return false;
      if (advanced.type && row.type_label !== advanced.type) return false;
      if (advanced.year && !(row.opening_date || "").startsWith(advanced.year)) return false;
      if (advanced.client && ![row.client_name, row.name].join(" ").toLowerCase().includes(advanced.client.toLowerCase())) return false;
      return true;
    });
    // Colpo d'occhio: massimo 8 pratiche, il resto con "Vedi tutte".
    return scopeAll || filteredView ? filtered : filtered.slice(0, 8);
  }, [gantt, quick, advanced, scopeAll, cadastralFilter, filteredView]);
  const years = useMemo(() => [...new Set((gantt?.rows || []).map(row => (row.opening_date || "").slice(0, 4)).filter(Boolean))].sort().reverse(), [gantt]);

  const kpi = control?.kpi || { to_recover: 0, within_week: 0, to_invoice: 0, to_invoice_count: 0 };
  const sister = useMemo(() => collectSisterChecks(data.practices || []), [data.practices]);
  const metrics = layout.economics_metrics || DEFAULT_ECONOMICS_METRICS;
  const showSummary = canViewEconomics && layout.show_economics !== false && metrics.length > 0;
  const invoiceInSummary = showSummary && metrics.includes("to_invoice");
  const greeting = useMemo(() => buildGreeting({ name: user.name, kpi, sister, events: studioEvents, signed: signed.length, loading: !control,
    waiting: control?.waiting?.total || 0, anomalies: control?.anomalies_total || 0, tracked: control?.practices_tracked || 0,
    activities: [...(data.activities || []), ...(data.deadlines || [])], practices: data.practices || [], showMoney: canViewEconomics }),
  [user.name, control, studioEvents, sister, signed.length, data.activities, data.deadlines, data.practices, canViewEconomics]); // eslint-disable-line react-hooks/exhaustive-deps
  const types = [...new Set((gantt?.rows || []).map(row => row.type_label).filter(Boolean))];

  function openTarget(kind, row) {
    if (kind === "practice") openRecord("practices", row.id);
    else if (kind === "economics") go("finance");
    else go("deadlines");
    setSelected(null);
  }

  return <div className="control-room overview practice-workspace" data-testid="overview-dashboard">
    <header className="cr-header" data-testid="cr-header">
      <div>
        <h1 data-testid="cr-greeting">{greeting.title}</h1>
        <p data-testid="cr-sentence">{greeting.sentence}</p>
      </div>
      <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
        <button className="cr-tune-btn" data-testid="cr-tune" onClick={() => setTuning(!tuning)}><SlidersHorizontal size={13} /> Personalizza</button>
        <AiPrioritiesButton loading={ai.loading} onClick={runAi} />
      </div>
    </header>

    {tuning && <section className="cr-card" data-testid="cr-tuning">
      <header><h3>Cosa vedere in dashboard</h3></header>
      <div className="gantt-advanced" style={{ padding: 0 }}>
        {[["show_gantt", "Cronoprogramma"], ["show_economics", "Riepilogo Economia"], ["show_waiting", "Attese esterne"], ["show_anomalies", "Da controllare"]].filter(([key]) => key !== "show_economics" || canViewEconomics).map(([key, label]) =>
          <label key={key} className="quote-switch" style={{ textTransform: "none", letterSpacing: 0, fontWeight: 400 }}>{label}
            <input type="checkbox" data-testid={`cr-tune-${key}`} checked={layout[key] !== false} onChange={event => persist({ [key]: event.target.checked })} />
          </label>)}
        <label>Periodo predefinito
          <select data-testid="cr-tune-range" value={layout.gantt_range} onChange={event => persist({ gantt_range: event.target.value })}>
            {RANGES.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
          </select>
        </label>
      </div>
      {canViewEconomics && <EconomicsMetricsPicker selected={layout.economics_metrics || DEFAULT_ECONOMICS_METRICS} onChange={economics_metrics => persist({ economics_metrics })} />}
    </section>}
    {!!preferencesSaving && <p role="status" data-testid="cr-preferences-saving" className="muted">Salvataggio preferenze…</p>}
    {preferenceError && <div className="error" role="alert" data-testid="cr-preferences-error">{preferenceError}<button className="ghost" data-testid="cr-preferences-retry" onClick={() => persist(layout)}>Riprova</button></div>}

    <div className="cr-strip" data-testid="cr-kpi-strip">
      <button className={`cr-stat ${kpi.to_recover ? "alert" : ""}`} data-testid="cr-kpi-recover" onClick={() => go("deadlines")} title="Attività dello studio oltre termine">
        <AlertCircle size={14} /><strong data-testid="cr-kpi-recover-value">{kpi.to_recover}</strong><span>da recuperare</span></button>
      <button className="cr-stat" data-testid="cr-kpi-week" onClick={() => go("deadlines")} title="Azioni da pianificare nei prossimi 7 giorni">
        <CalendarCheck size={14} /><strong data-testid="cr-kpi-week-value">{kpi.within_week}</strong><span>entro 7 giorni</span></button>
      {!invoiceInSummary && <button className="cr-stat money" data-testid="cr-kpi-invoice" onClick={() => go("practices:to-invoice")} title="Quote maturate da fatturare">
        <Euro size={14} /><strong data-testid="cr-kpi-invoice-value">{euro(kpi.to_invoice)}</strong><span>da fatturare</span></button>}
      {!!signed.length && <button className="cr-stat signed" data-testid="dashboard-signed-quotes" onClick={() => go("estimates")}
        title={signed.slice(0, 3).map(row => `n° ${row.number} · ${row.signature.name} · ${formatDate((row.signature.signed_at || "").slice(0, 10))}`).join("\n")}>
        <FileSignature size={14} /><strong>{signed.length}</strong><span data-testid="dashboard-signed-open">{signed.length === 1 ? "incarico firmato da confermare" : "incarichi firmati da confermare"}</span><ArrowUpRight size={13} /></button>}
    </div>

    {layout.show_gantt !== false && <section className="cr-panel" data-testid="cr-gantt-panel">
      <div className="cr-panel-head">
        <div>
          <h2>Cronoprogramma pratiche</h2>
          <p>Colpo d'occhio sui prossimi mesi{gantt ? ` · ${rows.length} pratiche` : ""}.</p>
        </div>
        <div className="cr-toggle" data-testid="cr-view-toggle">
          <button className={layout.gantt_view !== "list" ? "on" : ""} data-testid="cr-view-gantt" onClick={() => persist({ gantt_view: "gantt" })}>Vista Gantt</button>
          <button className={layout.gantt_view === "list" ? "on" : ""} data-testid="cr-view-list" onClick={() => persist({ gantt_view: "list" })}>Vista elenco</button>
          <span className="gantt-range" data-testid="cr-range">
            {RANGES.map(([value, label]) => <button key={value} className={layout.gantt_range === value ? "on" : ""}
              data-testid={`cr-range-${value}`} onClick={() => persist({ gantt_range: value })}>{label}</button>)}
          </span>
        </div>
      </div>
      <div className="gantt-toolbar">
        <GanttGuide />
        {QUICK.map(([key, label]) => <button key={key} className={`gantt-chip ${quick === key ? "on" : ""}`}
          data-testid={`cr-filter-${key}`} onClick={() => setQuick(key)}>{label}</button>)}
        <span className="spacer" />
        <button className={`gantt-chip ${advancedOpen ? "on" : ""}`} data-testid="cr-advanced-toggle" onClick={() => setAdvancedOpen(!advancedOpen)}>
          <SlidersHorizontal size={12} /> Filtri avanzati
        </button>
        <button className={`gantt-chip ${scopeAll ? "on" : ""}`} data-testid="cr-scope-all" onClick={() => setScopeAll(!scopeAll)}>{scopeAll ? "Solo rilevanti" : "Vedi tutte"}</button>
      </div>
      {advancedOpen && <div className="gantt-advanced" data-testid="cr-advanced">
        <label>Responsabile<select data-testid="cr-adv-responsible" value={advanced.responsible} onChange={e => setAdvanced({ ...advanced, responsible: e.target.value })}><option value="">Tutti</option>{team.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}</select></label>
        <label>Tipologia<select data-testid="cr-adv-type" value={advanced.type} onChange={e => setAdvanced({ ...advanced, type: e.target.value })}><option value="">Tutte</option>{types.map(t => <option key={t}>{t}</option>)}</select></label>
        <label>Committente o pratica<input data-testid="cr-adv-client" placeholder="Es. SUD MAGLIA" value={advanced.client} onChange={e => setAdvanced({ ...advanced, client: e.target.value })} /></label>
        <label>Anno di apertura<select data-testid="cr-adv-year" value={advanced.year} onChange={e => setAdvanced({ ...advanced, year: e.target.value })}><option value="">Tutti</option>{years.map(y => <option key={y}>{y}</option>)}</select></label>
        <label>Stato Catasto<select data-testid="cr-adv-catasto" value={advanced.catasto} onChange={e => setAdvanced({ ...advanced, catasto: e.target.value })}><option value="">Tutti gli stati</option>{CAT_STATES.map(s => <option key={s}>{s}</option>)}</select></label>
        <label>Inizio cronoprogramma<input type="date" data-testid="cr-adv-start-date" value={startDate} onChange={e => setStartDate(e.target.value)} /></label>
        <button className="gantt-chip" data-testid="cr-adv-reset" onClick={() => { setStartDate(""); setAdvanced({ responsible: "", type: "", client: "", year: "", invoice: false, catasto: "" }); setQuick("all"); }}>Azzera filtri</button>
        <label className="quote-switch" style={{ textTransform: "none", letterSpacing: 0, fontWeight: 400, alignSelf: "end" }}>Solo con quote da fatturare
          <input type="checkbox" data-testid="cr-adv-invoice" checked={advanced.invoice} onChange={e => setAdvanced({ ...advanced, invoice: e.target.checked })} />
        </label>
      </div>}
      {ganttError && <p role="alert" className="error" data-testid="cr-gantt-error">{ganttError}</p>}
      {layout.gantt_view === "list"
        ? <ListView rows={rows} onSelect={row => { setSelected(row); setDetail(null); }} />
        : <GanttChart data={gantt ? { ...gantt, rows } : null} selectedId={selected?.id}
          onSelect={row => { setSelected(row); setDetail(null); }}
          onDetail={(row, phase) => { setSelected(row); setDetail(phase); }} />}
      <div className="cr-mobile-hint" data-testid="cr-mobile-hint">
        <span>Il cronoprogramma è disponibile su schermo grande o in orizzontale.</span>
        <button className="gantt-chip" onClick={() => persist({ gantt_view: "list" })}>Apri elenco</button>
      </div>
    </section>}

    <NextActionsCard rows={(control?.next_actions || []).filter(row => !advanced.client || [row.client, row.practice, row.text].join(" ").toLowerCase().includes(advanced.client.toLowerCase()))} onOpen={id => openRecord("practices", id)} onAll={() => go("deadlines")} />

    <SisterChecks checks={sister} title="Controlli Sister catastali" limit={6} testId="dashboard-sister-checks" onOpenPractice={id => openRecord("practices", id)} />

    {showSummary && <EconomicsSummary selected={metrics} onOpen={() => go("finance")} />}

    <div className="overview-actions">
      {[["practices", "Nuova pratica", "green", FolderPlus], ["deadlines", "Nuova attività", "blue", CalendarPlus],
      ["advances", "Nuovo acconto", "amber", Euro], ["clients", "Nuovo cliente", "teal", UserPlus]]
        .map(([key, label, color, Icon]) => <button className={`action-tile ${color}`} key={key} data-testid={`dashboard-new-${key}`} onClick={() => onCreate(key)}><Icon size={23} /><span>{label}</span><ArrowUpRight size={14} /></button>)}
    </div>

    <div className="cr-modules">
      <button data-testid="cr-modules-toggle" onClick={() => { setModules(!modules); persist({ show_modules: !modules }); }}>
        Approfondimenti · attese esterne, anomalie e moduli <ChevronDown size={16} style={{ transform: modules ? "rotate(180deg)" : "none", transition: "transform .2s ease" }} />
      </button>
      {modules && <div className="cr-modules-body">
        <div className="cr-cards">
          {layout.show_waiting !== false && <WaitingCard waiting={control?.waiting} onOpen={() => { setQuick("esterne"); setModules(false); }} />}
          {layout.show_anomalies !== false && <AnomaliesCard rows={control?.anomalies || []} total={control?.anomalies_total || 0}
            onOpen={id => openRecord("practices", id)} onAll={() => go("practices")} />}
          <WorkloadCard rows={control?.workload || []} />
        </div>
        <OtherModules data={data} team={team} go={go} openRecord={openRecord} />
      </div>}
    </div>

    <PracticePreview row={selected} detail={detail} showEconomics={canViewEconomics} onClose={() => { setSelected(null); setDetail(null); }} onOpen={openTarget} />
    <AiPrioritiesModal open={ai.open} loading={ai.loading} priorities={ai.rows} error={ai.error} onClose={() => setAi({ ...ai, open: false })} />
  </div>;
}
